import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DateUtils from 'js/lib/DateUtils';
import { DataSource } from 'new/entities/DataSource';
import { fetchResourceMetricsAggregate, fetchResourceMetricsPerDay } from 'js/actions';
import { RESOURCE_TYPE_SOURCE } from 'js/constants/resourceTypes';
import { MetricsUtil } from 'new/utils/MetricsUtil';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import {
  GET_RESOURCE_METRICS_AGGREGATE_REQUEST,
  GET_RESOURCE_METRICS_BY_DAYS_REQUEST,
} from 'js/constants/actionTypes';
import { MetricsApiDateRange } from '@modules/monitor/resource-triage/components/MetricsTrendPanel';

type UseSourceOverviewMetrics = {
  metricsByDate: any[] | null;
  loadResourceMetricsAggregate: () => void | null;
  isLoading: boolean;
  loadResourceMetricsPerDay: (range: MetricsApiDateRange) => void | null;
  metrics: Record<string, string> | null;
};

const NoResourceResponse = {
  metricsByDate: null,
  loadResourceMetricsAggregate: () => {},
  isLoading: false,
  loadResourceMetricsPerDay: () => {},
  metrics: MetricsUtil.handleLifetimeMetrics(null),
};

export const useSourceOverviewMetrics = (resource: DataSource): UseSourceOverviewMetrics => {
  const dispatch = useDispatch();
  const state = useSelector((state: any) => state);
  const isLoading = useIsLoading(
    GET_RESOURCE_METRICS_BY_DAYS_REQUEST,
    GET_RESOURCE_METRICS_AGGREGATE_REQUEST,
  );
  const resourceID = resource?.id;

  useEffect(() => {
    if (resourceID) {
      dispatch(fetchResourceMetricsAggregate(resourceID, RESOURCE_TYPE_SOURCE));
      dispatch(
        fetchResourceMetricsPerDay(
          resourceID,
          RESOURCE_TYPE_SOURCE,
          DateUtils.getPastDateRange(30),
        ),
      );
    }
  }, []);

  /**
   * if the resource was not provided for some reason
   * returning a mock object to avoid UI crashing
   */
  if (!resource || typeof resource !== 'object')
    return NoResourceResponse as UseSourceOverviewMetrics;

  const loadResourceMetricsAggregate = () =>
    dispatch(fetchResourceMetricsAggregate(resourceID, RESOURCE_TYPE_SOURCE));

  const loadResourceMetricsPerDay = (range: MetricsApiDateRange) => {
    if (range?.start && range?.end) {
      dispatch(fetchResourceMetricsPerDay(resourceID, RESOURCE_TYPE_SOURCE, range));
    }
  };

  const metricsByResourceType = state.metrics[RESOURCE_TYPE_SOURCE];
  const metrics = metricsByResourceType ? metricsByResourceType[resourceID] : null;
  const aggregateMetrics = metrics?.agg?.metrics ?? null;
  const metricsByDate =
    metrics?.byDate && Object.keys(metrics.byDate).length !== 0 ? metrics.byDate : null;

  return {
    metricsByDate,
    loadResourceMetricsAggregate,
    isLoading,
    loadResourceMetricsPerDay,
    metrics: MetricsUtil.handleLifetimeMetrics(aggregateMetrics),
  };
};
